import React, { useState } from 'react';
import styles from './WerkplanningModule.module.css';
import { PlanningItem, PlanningView } from './types';
import { TimelineView } from './TimelineView';

import { ListView } from './ListView';
import { AddItemModal } from './AddItemModal';
import { BackwardPlanningModal } from './BackwardPlanningModal';
import { EditItemModal } from './EditItemModal';
import { exportToCSV } from './utils';
import { tasksAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useCrud } from '../../hooks/useCrud';
import { Project } from '../../types';

interface WerkplanningModuleProps {
  project: Project;
}

export const WerkplanningModule: React.FC<WerkplanningModuleProps> = ({ project }) => {
  const { user, isAdmin, isProjectManager } = useAuth();
  const [currentView, setCurrentView] = useState<PlanningView>('timeline');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState<PlanningItem | null>(null);
  const [showBackwardModal, setShowBackwardModal] = useState(false);
  const [showDependencyAlert, setShowDependencyAlert] = useState(false);
  const [dependentItems, setDependentItems] = useState<PlanningItem[]>([]);

  // Use CRUD hook for data management
  const {
    items: tasks,
    loading,
    error,
    createItem,
    updateItem,
    deleteItem
  } = useCrud<any>({
    fetchFn: tasksAPI.getByProject,
    createFn: tasksAPI.create,
    updateFn: tasksAPI.update,
    deleteFn: tasksAPI.delete,
    projectId: project.id
  });

  // Map backend types to frontend Dutch types (supports both English and Dutch input)
  const mapTypeToDutch = (type: string): PlanningItem['type'] => {
    const typeMap: Record<string, PlanningItem['type']> = {
      // English to Dutch mappings
      'task': 'taak',
      'milestone': 'mijlpaal',
      'meeting': 'vergadering',
      'deadline': 'deadline',
      'event': 'bijeenkomst',
      'review': 'rapport',
      'delivery': 'rapport',
      'other': 'overig',
      // Dutch passthrough (Task model uses Dutch enums)
      'taak': 'taak',
      'mijlpaal': 'mijlpaal',
      'communicatie': 'communicatie',
      'bijeenkomst': 'bijeenkomst',
      'vergadering': 'vergadering',
      'presentatie': 'presentatie',
      'rapport': 'rapport',
      'overig': 'overig'
    };
    return typeMap[type] || 'taak';
  };

  // Map backend tasks to PlanningItem format
  const planningItems: PlanningItem[] = tasks.map((task: any) => ({
    id: task._id || task.id,
    type: mapTypeToDutch(task.type || 'task'),
    title: task.title,
    date: new Date(task.dueDate).toISOString().split('T')[0],
    time: task.time || '',
    targetAudience: task.targetAudience || '',
    channel: task.channel || '',
    responsible: task.assignee?.name || task.responsible || '',
    status: task.status === 'completed' ? 'Voltooid' :
            task.status === 'in_progress' ? 'In uitvoering' :
            task.status === 'todo' ? 'Gepland' :
            task.status === 'planned' ? 'Gepland' :
            task.status === 'blocked' ? 'In voorbereiding' :
            task.status === 'review' ? 'In voorbereiding' :
            task.status === 'cancelled' ? 'Geannuleerd' :
            task.status === 'overdue' ? 'Verlopen' : 'Gepland',
    location: task.location?.address || (typeof task.location === 'string' ? task.location : ''),
    dependencies: task.dependencies || [],
    description: task.description,
    priority: task.priority,
    werkCategorie: task.werkCategorie || 'project',
  }));

  // Map frontend Dutch types back to backend English types
  const mapTypeToEnglish = (type: string): string => {
    const typeMap: Record<string, string> = {
      'taak': 'task',
      'mijlpaal': 'milestone',
      'vergadering': 'meeting',
      'deadline': 'deadline',
      'bijeenkomst': 'event',
      'communicatie': 'event',
      'presentatie': 'event',
      'rapport': 'delivery',
      'overig': 'other'
    };
    return typeMap[type] || 'task';
  };

  const handleAddItem = async (item: Omit<PlanningItem, 'id'>) => {
    const taskData = {
      project: project.id,
      type: mapTypeToEnglish(item.type),
      title: item.title,
      dueDate: new Date(`${item.date}T${item.time || '00:00'}`),
      time: item.time,
      targetAudience: item.targetAudience,
      channel: item.channel,
      responsible: item.responsible,
      status: item.status === 'Voltooid' ? 'completed' :
              item.status === 'In uitvoering' ? 'in_progress' :
              item.status === 'Geannuleerd' ? 'cancelled' : 'planned',
      location: item.location,
      dependencies: item.dependencies,
      description: item.description,
      priority: (item as any).priority || 'medium',
      werkCategorie: item.werkCategorie || 'project',
    };

    await createItem(taskData);
    setShowAddModal(false);
  };

  const handleEditItem = async (id: string, updatedItem: PlanningItem) => {
    const taskData = {
      type: mapTypeToEnglish(updatedItem.type),
      title: updatedItem.title,
      dueDate: new Date(`${updatedItem.date}T${updatedItem.time || '00:00'}`),
      time: updatedItem.time,
      targetAudience: updatedItem.targetAudience,
      channel: updatedItem.channel,
      responsible: updatedItem.responsible,
      status: updatedItem.status === 'Voltooid' ? 'completed' :
              updatedItem.status === 'In uitvoering' ? 'in_progress' :
              updatedItem.status === 'Geannuleerd' ? 'cancelled' : 'planned',
      location: updatedItem.location,
      dependencies: updatedItem.dependencies,
      description: updatedItem.description,
      priority: (updatedItem as any).priority || 'medium',
      werkCategorie: updatedItem.werkCategorie || 'project',
    };

    await updateItem(id, taskData);
    checkDependencies(updatedItem);
    setShowEditModal(false);
    setEditingItem(null);
  };

  const openEditModal = (item: PlanningItem) => {
    setEditingItem(item);
    setShowEditModal(true);
  };

  const handleDeleteItem = async (id: string) => {
    await deleteItem(id);
  };

  const checkDependencies = (changedItem: PlanningItem) => {
    const dependent = planningItems.filter(item => 
      item.dependencies?.includes(changedItem.id)
    );
    
    if (dependent.length > 0) {
      setDependentItems(dependent);
      setShowDependencyAlert(true);
    }
  };

  const handleBackwardPlanning = async (items: Omit<PlanningItem, 'id'>[]) => {
    // Create items via API one by one
    for (const item of items) {
      await createItem({
        project: project.id,
        type: mapTypeToEnglish(item.type),
        title: item.title,
        dueDate: new Date(`${item.date}T${item.time || '00:00'}`),
        time: item.time,
        targetAudience: item.targetAudience,
        channel: item.channel,
        responsible: item.responsible,
        status: item.status === 'Voltooid' ? 'completed' : 'planned',
        dependencies: item.dependencies
      });
    }
    setShowBackwardModal(false);
  };

  const handleExport = () => {
    exportToCSV(planningItems, 'werkplanning');
  };

  const viewComponents = {
    timeline: <TimelineView items={planningItems} onEditItem={openEditModal} onDeleteItem={handleDeleteItem} />,
    list: <ListView items={planningItems} onEditItem={openEditModal} onDeleteItem={handleDeleteItem} />
  };

  if (loading) {
    return (
      <div className={styles.werkplanning}>
        <div className={styles.loading}>Planning laden...</div>
      </div>
    );
  }

  return (
    <div className={styles.werkplanning}>
      <div className={styles.header}>
        <h1>Werkplanning</h1>
        
        <div className={styles.controls}>
          {(isAdmin || isProjectManager) && (
            <button className={styles.btnPrimary} onClick={() => setShowAddModal(true)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Nieuw item
            </button>
          )}
          <button className={styles.btnSecondary} onClick={() => setShowBackwardModal(true)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="11 17 6 12 11 7" />
              <polyline points="18 17 13 12 18 7" />
            </svg>
            Terugrekenen vanaf deadline
          </button>
          <button className={styles.btnTertiary} onClick={handleExport}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="12" y1="18" x2="12" y2="12" />
              <line x1="9" y1="15" x2="12" y2="12" />
              <line x1="15" y1="15" x2="12" y2="12" />
            </svg>
            Exporteren
          </button>
        </div>
      </div>

      <div className={styles.viewToggle}>
        <button 
          className={`${styles.viewBtn} ${currentView === 'timeline' ? styles.active : ''}`}
          onClick={() => setCurrentView('timeline')}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
          Tijdlijn
        </button>
        <button
          className={`${styles.viewBtn} ${currentView === 'list' ? styles.active : ''}`}
          onClick={() => setCurrentView('list')}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="8" y1="6" x2="21" y2="6" />
            <line x1="8" y1="12" x2="21" y2="12" />
            <line x1="8" y1="18" x2="21" y2="18" />
            <line x1="3" y1="6" x2="3.01" y2="6" />
            <line x1="3" y1="12" x2="3.01" y2="12" />
            <line x1="3" y1="18" x2="3.01" y2="18" />
          </svg>
          Lijst
        </button>
      </div>

      <div className={styles.content}>
        {viewComponents[currentView]}
      </div>

      {showAddModal && (
        <AddItemModal
          onClose={() => setShowAddModal(false)}
          onSave={handleAddItem}
        />
      )}

      {showEditModal && editingItem && (
        <EditItemModal
          item={editingItem}
          onClose={() => {
            setShowEditModal(false);
            setEditingItem(null);
          }}
          onSave={(item) => handleEditItem(editingItem.id, item)}
          onDelete={handleDeleteItem}
        />
      )}

      {showBackwardModal && (
        <BackwardPlanningModal
          onClose={() => setShowBackwardModal(false)}
          onApply={handleBackwardPlanning}
        />
      )}

      {showDependencyAlert && (
        <div className={styles.alert}>
          <div className={styles.alertIcon}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>
          <div className={styles.alertContent}>
            <div className={styles.alertTitle}>Aanpassing heeft impact op andere items</div>
            <div className={styles.alertMessage}>
              De volgende items zijn afhankelijk van deze wijziging:
              <ul>
                {dependentItems.map(item => (
                  <li key={item.id}>{item.title} ({new Date(item.date).toLocaleDateString('nl-NL')})</li>
                ))}
              </ul>
            </div>
            <div className={styles.alertActions}>
              <button onClick={() => setShowDependencyAlert(false)}>Annuleren</button>
              <button onClick={() => {
                // Apply dependency changes logic here
                setShowDependencyAlert(false);
              }}>Aanpassen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};